/**
 * Resource slice — global resource configuration for financial analysis.
 *
 * Persists ResourceConfig in localStorage. Computed metrics are derived
 * on-the-fly from config + node data (not stored).
 */

import type { StateCreator } from 'zustand';
import type { ResourceConfig } from '../types/resource-config';
import { DEFAULT_RESOURCE_CONFIG } from '../types/resource-config';
import type { AdjacencyStoreState } from './store-types';

const STORAGE_KEY = 'iwt-resource-config';

// ── Slice Interface ──────────────────────────────────────────────────

export interface ResourceSlice {
  resourceConfig: ResourceConfig;
  budgetImportedAt: string | null;

  updateResourceConfig: (partial: Partial<ResourceConfig>) => void;
  resetResourceConfig: () => void;
  setBudgetImportedAt: (timestamp: string) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────

function loadConfig(): ResourceConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ResourceConfig>;
      return { ...DEFAULT_RESOURCE_CONFIG, ...parsed };
    }
  } catch {
    // Ignore parse errors, fall through to default
  }
  return { ...DEFAULT_RESOURCE_CONFIG };
}

function saveConfig(config: ResourceConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

// ── Implementation ───────────────────────────────────────────────────

export const createResourceSlice: StateCreator<
  AdjacencyStoreState,
  [],
  [],
  ResourceSlice
> = (set, get) => ({
  resourceConfig: loadConfig(),
  budgetImportedAt: localStorage.getItem('iwt-budget-imported-at'),

  updateResourceConfig: (partial) => {
    const current = get().resourceConfig;
    const updated = { ...current, ...partial };
    saveConfig(updated);
    set({ resourceConfig: updated });
  },

  resetResourceConfig: () => {
    const defaults = { ...DEFAULT_RESOURCE_CONFIG };
    saveConfig(defaults);
    set({ resourceConfig: defaults });
  },

  setBudgetImportedAt: (timestamp) => {
    localStorage.setItem('iwt-budget-imported-at', timestamp);
    set({ budgetImportedAt: timestamp });
  },
});
